import React from "react";
import { ArrowRight, Linkedin, Facebook, MessageCircle, Mail, Sparkles, Award, FileText } from "lucide-react";
import { PROFILE } from "../lib/content";
import { useContactModal } from "../lib/contactModal";
import ParticleField from "./ParticleField";

const Hero = () => {
  const { openModal } = useContactModal();
  const go = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section
      id="hero"
      data-testid="section-hero"
      className="hero-dark relative min-h-[100svh] pt-24 pb-12 sm:pt-20 sm:pb-10 overflow-hidden flex flex-col"
    >
      {/* Animated connected-dots particle field */}
      <ParticleField />

      {/* Decorative orbits */}
      <div className="orbit-ring" style={{ width: 560, height: 560, top: "-180px", left: "-200px" }} />
      <div className="orbit-ring animate-spin-slow" style={{ width: 380, height: 380, bottom: "-100px", right: "10%" }} />

      {/* Floating accent dots */}
      <div
        className="absolute hidden lg:block animate-pulse-soft float-y-rev"
        style={{
          width: 8, height: 8, borderRadius: 99, background: "#5C8470",
          top: "70%", right: "6%",
          boxShadow: "0 0 40px 8px rgba(92,132,112,0.45)",
        }}
        aria-hidden
      />
      <div
        className="absolute hidden lg:block float-y"
        style={{ top: "20%", right: "12%", color: "rgba(250,248,245,0.4)" }}
        aria-hidden
      >
        <Sparkles size={20} />
      </div>
      <div
        className="absolute hidden lg:block float-y-rev"
        style={{ bottom: "20%", left: "12%", color: "rgba(250,248,245,0.4)" }}
        aria-hidden
      >
        <Award size={22} />
      </div>

      <div className="relative max-w-7xl mx-auto w-full px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-12 items-center lg:items-start flex-1 min-h-0 pt-2 lg:pt-8">
        {/* Text */}
        <div className="lg:col-span-7 order-2 lg:order-1 text-alabaster">
          <div
            data-testid="hero-eyebrow"
            className="eyebrow !text-terracotta-soft inline-flex items-center gap-3"
          >
            <span className="block w-10 h-px bg-terracotta-soft" />
            Yazid M. Al-Badarin
            <span
              className="inline-block w-2.5 h-2.5 rounded-full bg-terracotta float-y animate-pulse-soft"
              style={{ boxShadow: "0 0 18px 4px rgba(245,187,31,0.6)" }}
              aria-hidden
            />
          </div>

          <h1
            data-testid="hero-heading"
            className="font-display mt-4 text-[30px] sm:text-5xl lg:text-[58px] xl:text-[68px] font-bold leading-[1.04] tracking-tight"
            aria-label="Driving Innovation through Education and Technology"
          >
            <span className="block whitespace-nowrap animate-fade-up" style={{ animationDelay: "150ms" }}>
              Driving Innovation
            </span>
            <span className="block whitespace-nowrap animate-fade-up text-terracotta-soft" style={{ animationDelay: "350ms" }}>
              through Education
            </span>
            <span className="block whitespace-nowrap animate-fade-up" style={{ animationDelay: "550ms" }}>
              and Technology
            </span>
          </h1>

          <p
            data-testid="hero-subheading"
            className="mt-5 text-sm sm:text-base text-alabaster/85 leading-relaxed font-display font-medium animate-fade-up whitespace-pre-line"
            style={{ animationDelay: "750ms" }}
          >
            {PROFILE.pitch}
          </p>

          <div
            className="mt-6 flex flex-wrap items-center gap-3 sm:gap-4 animate-fade-up"
            style={{ animationDelay: "900ms" }}
          >
            <button
              data-testid="hero-cta-contact"
              onClick={openModal}
              className="btn-primary"
            >
              Join Forces
              <ArrowRight size={16} />
            </button>

            {/* Inline social cluster */}
            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 sm:ml-3 relative z-20">
              <a data-testid="hero-social-linkedin" href={PROFILE.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-terracotta text-alabaster flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(245,187,31,0.6)] hover:bg-terracotta-hover hover:-translate-y-0.5 transition-all" aria-label="LinkedIn"><Linkedin size={15} /></a>
              <a data-testid="hero-social-facebook" href={PROFILE.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-terracotta text-alabaster flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(245,187,31,0.6)] hover:bg-terracotta-hover hover:-translate-y-0.5 transition-all" aria-label="Facebook"><Facebook size={15} /></a>
              <a data-testid="hero-social-whatsapp" href={PROFILE.whatsapp1Link} target="_blank" rel="noreferrer" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-terracotta text-alabaster flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(245,187,31,0.6)] hover:bg-terracotta-hover hover:-translate-y-0.5 transition-all" aria-label="WhatsApp"><MessageCircle size={15} /></a>
              <a data-testid="hero-social-email" href={`mailto:${PROFILE.email}`} className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-terracotta text-alabaster flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(245,187,31,0.6)] hover:bg-terracotta-hover hover:-translate-y-0.5 transition-all" aria-label="Email"><Mail size={15} /></a>
              <a data-testid="hero-social-researchgate" href={PROFILE.researchgate} target="_blank" rel="noreferrer" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-terracotta text-alabaster flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(245,187,31,0.6)] hover:bg-terracotta-hover hover:-translate-y-0.5 transition-all font-display font-bold text-[13px] tracking-tight" aria-label="ResearchGate">
                R<sup className="text-[9px] -ml-0.5">G</sup>
              </a>
              <a data-testid="hero-social-cv" href={PROFILE.cv} target="_blank" rel="noreferrer" download className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-terracotta text-alabaster flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(245,187,31,0.6)] hover:bg-terracotta-hover hover:-translate-y-0.5 transition-all" aria-label="Download CV"><FileText size={15} /></a>
            </div>
          </div>
        </div>

        {/* Portrait disc */}
        <div className="lg:col-span-5 order-1 lg:order-2 flex items-start justify-center lg:justify-start lg:pl-4">
          <div className="portrait-disc relative animate-fade-up shrink-0 w-[210px] sm:w-[300px] lg:w-[380px] xl:w-[440px] 2xl:w-[520px]" style={{ animationDelay: "200ms" }}>
            {/* Outer ring */}
            <div className="disc-ring animate-spin-slow" />
            {/* The portrait */}
            <img
              data-testid="hero-portrait"
              src={PROFILE.portraitMain}
              alt="Yazid M. Al-Badarin"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
